import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Store for auth state
interface AuthState {
  accessToken: string;
  refreshToken: string;
  tokenExpiry: number | null;
  isAuthenticated: boolean;
  isRefreshing: boolean;
  setTokens: (
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
  ) => void;
  clearTokens: () => void;
  setRefreshing: (refreshing: boolean) => void;
  checkTokenExpiry: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: '',
      refreshToken: '',
      tokenExpiry: null,
      isAuthenticated: false,
      isRefreshing: false,

      setTokens: (
        accessToken: string,
        refreshToken: string,
        expiresIn: number,
      ) => {
        let tokenExpiry = null;

        // Calculate token expiry time
        try {
          if (accessToken && expiresIn) {
            // Calculate expiry from expiresIn seconds
            tokenExpiry = Date.now() + expiresIn * 1000;
          }
        } catch (error) {
          console.error('Failed to process token expiry:', error);
        }

        set({
          accessToken,
          refreshToken,
          tokenExpiry,
          isAuthenticated: !!accessToken,
        });
      },

      clearTokens: () =>
        set({
          accessToken: '',
          refreshToken: '',
          tokenExpiry: null,
          isAuthenticated: false,
          isRefreshing: false,
        }),

      setRefreshing: (refreshing: boolean) =>
        set({
          isRefreshing: refreshing,
        }),

      // Check if token is expired or about to expire (within 5 minutes)
      checkTokenExpiry: () => {
        const { tokenExpiry } = get();

        if (!tokenExpiry) {
          return false; // No token or invalid token
        }

        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;

        // If token expires in less than 5 minutes or is already expired
        if (tokenExpiry - now < fiveMinutes) {
          return false;
        }

        return true; // Token is valid
      },
    }),
    {
      name: 'auth-storage',
      // Only persist in session storage for security
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) return null;
          return JSON.parse(str);
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    },
  ),
);
